import { Image, List } from 'antd';
import moment from 'moment';
import React, { PureComponent } from 'react';
import { Notes } from '../../api/fetchAllMedicalRecordsInADoc';
import { UIDocumentEntity } from '../../pages/MedicalRecordsRightPanel';
import { DoctorProfileData } from '../../api/fetchAllDoctorDetailsByIds';
import { MR_ID } from './MediaViewerNew';
import AppLoader from './AppLoader';

type Props = {
    allNotes?: Notes[];
    nextPaginationToken?: string;
    fetchAndStoreNotes: (
        oid: string,
        document_id: string,
        next_token?: string | undefined,
        callback?: () => void,
    ) => Promise<void>;
    documentFolder?: UIDocumentEntity;
    doctorsDetails: DoctorProfileData;
    isNotesLoading?: boolean;
};
type State = {
    loading: boolean;
};

export class MedicalRecordsNotes extends PureComponent<Props, State> {
    listRef: React.RefObject<any>;
    constructor(props: Props) {
        super(props);
        this.listRef = React.createRef();
    }

    state: State = {
        loading: false,
    };

    onLoadMore = () => {
        const { fetchAndStoreNotes, documentFolder, nextPaginationToken } = this.props;

        this.setState({
            loading: true,
        });

        const document_id = documentFolder?.document_id;
        const oid = documentFolder?.oid;

        if (oid && document_id && nextPaginationToken) {
            fetchAndStoreNotes(oid, document_id, nextPaginationToken, () => {
                this.setState({
                    loading: false,
                });
            });
        }
    };

    componentDidMount(): void {
        document
            ?.getElementById(MR_ID)
            ?.scrollTo(0, document?.getElementById(MR_ID)?.scrollHeight || 0);
    }

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>,
        snapshot?: any,
    ): void {
        if (prevProps.isNotesLoading !== this.props.isNotesLoading) {
            document
                ?.getElementById(MR_ID)
                ?.scrollTo(0, document?.getElementById(MR_ID)?.scrollHeight || 0);
        }
    }

    render() {
        const { allNotes, nextPaginationToken, doctorsDetails, isNotesLoading } = this.props;
        const { loading } = this.state;

        const loadMore = nextPaginationToken ? (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '4px 0px',
                    lineHeight: '16px',
                    borderRadius: '16px',
                }}
            >
                <button
                    onClick={this.onLoadMore}
                    className="secondary-btn-v3 p-8 flex cursor-pointer rounded-16 space-x-2 items-center"
                >
                    <span>load more</span>
                    <span>
                        {loading ? (
                            <svg
                                className="w-10 h-10 fill-darwin-accent-bright-blue-800  animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 384 512"
                                className="h-10 w-10 fill-darwin-accent-bright-blue-800 "
                            >
                                <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2V448c0 17.7 14.3 32 32 32s32-14.3 32-32V141.2L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                            </svg>
                        )}
                    </span>
                </button>
            </div>
        ) : null;

        if (isNotesLoading) {
            return (
                <div className="w-full py-200 flex items-center justify-center">
                    <AppLoader />
                </div>
            );
        }

        return (
            <div className={`${allNotes?.length ? 'pb-128' : 'overflow-hidden'}`}>
                <List
                    className="h-full w-full"
                    locale={{
                        emptyText: (
                            <div className="h-full py-200 w-full flex flex-col items-center justify-center space-y-8">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="41"
                                    height="41"
                                    viewBox="0 0 41 41"
                                    fill="none"
                                >
                                    <path
                                        d="M13.377 13C13.377 11.3594 14.6895 10 16.377 10H28.377C30.0176 10 31.377 11.3594 31.377 13V22.2812C31.377 23.0781 31.0488 23.8281 30.4863 24.3906L27.7207 27.1562C27.1582 27.7188 26.4082 28 25.6113 28H16.377C14.6895 28 13.377 26.6875 13.377 25V13ZM20.877 15.25V17.5H18.627C18.2051 17.5 17.877 17.875 17.877 18.25V19.75C17.877 20.1719 18.2051 20.5 18.627 20.5H20.877V22.75C20.877 23.1719 21.2051 23.5 21.627 23.5H23.127C23.502 23.5 23.877 23.1719 23.877 22.75V20.5H26.127C26.502 20.5 26.877 20.1719 26.877 19.75V18.25C26.877 17.875 26.502 17.5 26.127 17.5H23.877V15.25C23.877 14.875 23.502 14.5 23.127 14.5H21.627C21.2051 14.5 20.877 14.875 20.877 15.25Z"
                                        fill="#A3A3A3"
                                    />
                                    <path
                                        d="M22.752 30.25C23.3613 30.25 23.877 30.7656 23.877 31.375C23.877 32.0312 23.3613 32.5 22.752 32.5H15.252C11.6895 32.5 8.87695 29.6875 8.87695 26.125V15.625C8.87695 15.0156 9.3457 14.5 10.002 14.5C10.6113 14.5 11.127 15.0156 11.127 15.625V26.125C11.127 28.4219 12.9551 30.25 15.252 30.25H22.752ZM20.877 15.25C20.877 14.875 21.2051 14.5 21.627 14.5H23.127C23.502 14.5 23.877 14.875 23.877 15.25V17.5H26.127C26.502 17.5 26.877 17.875 26.877 18.25V19.75C26.877 20.1719 26.502 20.5 26.127 20.5H23.877V22.75C23.877 23.1719 23.502 23.5 23.127 23.5H21.627C21.2051 23.5 20.877 23.1719 20.877 22.75V20.5H18.627C18.2051 20.5 17.877 20.1719 17.877 19.75V18.25C17.877 17.875 18.2051 17.5 18.627 17.5H20.877V15.25Z"
                                        fill="#484848"
                                    />
                                </svg>

                                <span className="text-13 text-darwin-neutral_new-800 text-center">
                                    Click anywhere on the report in the left to a add note or note
                                    box below
                                </span>
                            </div>
                        ),
                    }}
                    header={loadMore}
                    itemLayout="vertical"
                    dataSource={allNotes}
                    renderItem={(notes) => {
                        return (
                            <div className="bg-white p-16 flex space-x-8 mb-8">
                                <div className="mt-4">
                                    <Image
                                        width={24}
                                        height={24}
                                        src={`https://a.eka.care/doctor-avatar/${notes?.author}`}
                                        className="mt-2 bg-neutral-black border border-light rounded-full"
                                        preview={{
                                            mask: (
                                                <span
                                                    role="img"
                                                    aria-label="eye"
                                                    className="anticon anticon-eye"
                                                >
                                                    <svg
                                                        viewBox="64 64 896 896"
                                                        focusable="false"
                                                        data-icon="eye"
                                                        width="1em"
                                                        height="1em"
                                                        fill="currentColor"
                                                        aria-hidden="true"
                                                    >
                                                        <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path>
                                                    </svg>
                                                </span>
                                            ),
                                            maskClassName: 'profile-pic-mask',
                                        }}
                                    />
                                </div>
                                <div className="w-full space-y-4">
                                    <div className="flex justify-between space-x-8 w-full">
                                        <div>
                                            <div className="font-700 text-14">
                                                {[
                                                    doctorsDetails?.[notes.author]?.name.f,
                                                    doctorsDetails?.[notes.author]?.name.l,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </div>
                                            <div className="text-13 text-darwin-neutrals-600">
                                                {' '}
                                                {doctorsDetails?.[notes.author]?.speciality}
                                            </div>
                                        </div>

                                        <div className="text-12 text-darwin-neutrals-600">
                                            {moment(notes.time * 1000).format(
                                                "DD MMM 'YY, hh:mm a",
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className="whitespace-preline flex flex-col tiny-mce text-15"
                                        style={{
                                            whiteSpace: 'pre-line',
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: notes.text || '',
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    }}
                />
            </div>
        );
    }
}

export default MedicalRecordsNotes;
